import React, { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import timeouts from '../../../_shared/constants/timeouts';
import ConditionDisclosureOverlay from './ConditionDisclosureOverlay';
import MediaGallery from '../MediaGallery';
import Loading from '../../../_shared/components/Loading';
import Alert from '../../../_shared/components/Alert';
import ReducerConstants from '../../../_shared/constants/reducers';
import renderLocale from '../../../_shared/helpers/renderLocale';
import MixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import useAnalytics from '../../../_shared/hooks/useAnalytics';
import useOpenMultiMediaGallery from '../../../_shared/hooks/useTrackMultiMediaGallery';

import StyledConditionsDisclosures, {
  StyledChecklist,
  StyledConditionHeading,
  StyledConditionGroupings,
  StyledConditionItems,
  StyledConditionIcon,
  StyledConditionTitle,
  StyledChecklistIcon,
  StyledDivider,
  StyledNotes,
  NotesHeading,
  StyledNotesIcon,
  StyledNotesTitle,
  StyledSectionDivider,
  StyledBadge,
  StyledDisclosureCountBadge,
  StyledThirdPartyInspected,
  StyledThirdPartyInspectedContent,
  StyledBox,
  StyledDisclosures,
  StyledDisclosuresLabel,
  StyledTitle,
  StyledSubTitle,
  StyledNothingReported,
  StyledNotesBox,
  StyledNotesDisclaimer,
  StyledMediaLink,
  StyledGalleryIcon,
  StyledMedia
} from './Styles';
import TRToast from '../../../_shared/helpers/TRToast';
import segmentConfig from '../../../_shared/constants/segmentConfig';

const propTypes = {
  conditionDisclosuresList: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string
    })
  )
};

const defaultProps = {
  conditionDisclosuresList: []
};

const ConditionsDisclosures = ({
  conditionDisclosuresList,
  tradeDetails,
  conditionDisclosureCount,
  mediaList,
  isLoading,
  hasErrors,
  tradeContext,
  tradeState
}) => {
  const track = useAnalytics();
  const [isOpen, setIsOpen] = useState(false);
  const [currentDisclosure, setCurrentDisclosure] = useState('');
  const [selectedPhotoId, setSelectedPhotoId] = useState();
  const [isOverlayOpen, setOverlayOpen] = useState(false);
  const auth = useSelector(state => state[ReducerConstants.AUTH], shallowEqual);
  const locale = renderLocale(auth);
  const trackMultiMediaGallery = useOpenMultiMediaGallery();

  const findMediaElementByLabel = (array, title) => {
    return array.find(
      element =>
        element.displayName.localeCompare(title, undefined, {
          sensitivity: 'accent'
        }) === 0
    );
  };

  const openOverlay = media => {
    if (!isOverlayOpen && mediaList?.meta && media) {
      const matchingMediaItem = findMediaElementByLabel(
        mediaList.meta.disclosurePhotos,
        media
      );
      if (matchingMediaItem) {
        setSelectedPhotoId(matchingMediaItem.id);
      }
      setOverlayOpen(true);

      trackMultiMediaGallery({
        tradeDetails,
        tradeState,
        mediaList,
        tradeContext,
        galleryOpenType: segmentConfig.galleryOpenType.damageThumbnails
      });
    }
  };

  const closeOverlay = () => {
    setOverlayOpen(false);
  };

  const openModal = disc => {
    if (!isOpen) {
      setIsOpen(true);
      setCurrentDisclosure(disc);
      track(MixpanelConfig.names.viewConditionChecklistFromVDP, {
        condition_category: disc.category
      });
    }
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  // TODO: Handle french translation string for this
  const notOnOverViewDisclosures = [
    'Any exterior photos showing damage',
    'Any interior photos showing damage',
    'Toute photo extérieure montrant des dommages',
    'Toute photo intérieure montrant des dommages'
  ];

  useEffect(() => {
    if (hasErrors) {
      TRToast(
        <Alert
          variant="error"
          messages={[<FormattedMessage id="vdp.error.general" />]}
        />,
        { autoClose: timeouts.ERROR }
      );
    }
  }, [hasErrors]);

  return isLoading || hasErrors ? (
    <StyledConditionsDisclosures
      id="condition"
      data-testid="vdpConditionsLoading"
    >
      <StyledBox>
        <StyledConditionHeading>
          <Loading width="300px" height="30px" />
        </StyledConditionHeading>
      </StyledBox>
      <StyledConditionGroupings>
        <Loading width="500px" height="200px" />
      </StyledConditionGroupings>
    </StyledConditionsDisclosures>
  ) : (
    <StyledConditionsDisclosures id="condition" data-testid="vdpConditions">
      <StyledBox>
        <StyledConditionHeading data-testid="vdpCondition-title">
          {/* Condition */}
          <FormattedMessage id="vdp.conditions.title" />
        </StyledConditionHeading>
        {tradeDetails.isAsIs && (
          <div className="vdp-asis">
            <StyledBadge testId="vdp-badge-as-is">
              {/* As-is */}
              <FormattedMessage id="vdp.asis" />
            </StyledBadge>
          </div>
        )}
        {conditionDisclosureCount > 0 && (
          <StyledDisclosureCountBadge>
            {conditionDisclosureCount}
          </StyledDisclosureCountBadge>
        )}
        {tradeDetails.isMscCertified && (
          <StyledThirdPartyInspectedContent>
            {/* Third Party Inspected */}
            <FormattedMessage id="vdp.conditions.thirdPartyInspected" />
            <StyledThirdPartyInspected icon="TR5-ThirdPartyInspected" />
          </StyledThirdPartyInspectedContent>
        )}
      </StyledBox>
      <ConditionDisclosureOverlay
        isOpen={isOpen}
        closeModal={closeModal}
        disclosure={currentDisclosure}
      />
      <MediaGallery
        isOpen={isOverlayOpen}
        closeOverlay={closeOverlay}
        mediaList={mediaList}
        tradeDetails={tradeDetails}
        preSelectedPhotoId={selectedPhotoId}
      />
      <StyledConditionGroupings>
        {conditionDisclosuresList.map(disclosure => {
          return (
            <React.Fragment key={disclosure.type}>
              <StyledConditionItems>
                <StyledConditionTitle>
                  <div className="vdpIcon">
                    <StyledConditionIcon icon={`TR5-${disclosure.category}`} />
                  </div>
                  <StyledTitle data-testid={`vdpCondition-${disclosure.type}`}>
                    {disclosure.type}
                    {locale && locale !== 'fr-CA' && (
                      <StyledSubTitle>condition</StyledSubTitle>
                    )}
                  </StyledTitle>
                  {!disclosure.disclosures.length && (
                    <StyledNothingReported className="notranslate">
                      {/* Nothing reported */}
                      <FormattedMessage id="vdp.conditions.nothingReported" />
                    </StyledNothingReported>
                  )}
                  <StyledChecklist
                    type="submit"
                    onClick={() => openModal(disclosure)}
                  >
                    {/* See checklist */}
                    <FormattedMessage id="vdp.conditions.seeChecklist" />
                    <StyledChecklistIcon icon="TR5-Next_chev" />
                  </StyledChecklist>
                </StyledConditionTitle>
                {disclosure.disclosures && disclosure.disclosures.length > 0 && (
                  <StyledDisclosures>
                    {disclosure.disclosures.map(disc => {
                      return notOnOverViewDisclosures.indexOf(disc) <= -1 &&
                        disc ? (
                        <StyledDisclosuresLabel key={disc}>
                          {disc}
                        </StyledDisclosuresLabel>
                      ) : null;
                    })}
                    {disclosure.media.map(media => {
                      return (
                        <StyledMedia key={media}>
                          <StyledGalleryIcon icon="TR5-Photo_camera" />
                          <StyledMediaLink onClick={() => openOverlay(media)}>
                            {media}
                          </StyledMediaLink>
                        </StyledMedia>
                      );
                    })}
                  </StyledDisclosures>
                )}
              </StyledConditionItems>
              <StyledSectionDivider />
            </React.Fragment>
          );
        })}
      </StyledConditionGroupings>
      <StyledDivider className="disclosureDivider" />
      <StyledConditionItems id="notesSection">
        <StyledNotesBox>
          <div className="vdpIcon">
            <StyledNotesIcon icon="TR5-InspectionNotes" />
          </div>
          <NotesHeading id="notesHeading">
            <StyledNotesTitle>
              {/* Inspection & dealer notes */}
              <FormattedMessage id="vdp.conditions.inspectionDealerNotes" />
            </StyledNotesTitle>
            <StyledNotesDisclaimer>
              {/* Always read & review all notes before bidding. */}
              <FormattedMessage id="vdp.conditions.alwaysReadNotes" />
            </StyledNotesDisclaimer>
          </NotesHeading>
        </StyledNotesBox>
        <StyledNotes isNotesAvailable={!!tradeDetails.notes}>
          {tradeDetails.notes ? (
            /* eslint-disable-next-line */
            <span dangerouslySetInnerHTML={{ __html: tradeDetails.notes }} />
          ) : (
            <FormattedMessage id="vdp.conditions.noNotes" />
          )}
        </StyledNotes>
      </StyledConditionItems>
    </StyledConditionsDisclosures>
  );
};

ConditionsDisclosures.propTypes = propTypes;
ConditionsDisclosures.defaultProps = defaultProps;

export default ConditionsDisclosures;
